import { useState } from 'react';
import { createPortal } from 'react-dom';
import { UserPlus, Check, Info, AlertTriangle, Link2, ClipboardList, User } from 'lucide-react';
import type { RegisterFormData, MatchedEntity } from '../../../../../stores/registerStore';
import { maskIdCard } from '../mask';
import { MatchedFieldBadges, ZoomAvatarButton, ZoomPreviewLayer } from '../shared';

/** 客户档案认领弹窗（注册时检测到系统中已有匹配档案） */
export function CustomerBindingModal({
  isOpen,
  onMismatch,
  customer,
  formData,
  onConfirm,
}: {
  isOpen: boolean;
  /** 用户判定「非本人信息」→ 打开二级处理弹窗 */
  onMismatch: () => void;
  customer: MatchedEntity;
  formData: RegisterFormData;
  onConfirm: () => void;
}) {
  // 头像放大预览（帮助用户核对照片判定是否本人）
  const [zoomAvatar, setZoomAvatar] = useState(false);
  // 命中明细快捷判断：某注册字段是否有命中记录
  const hitField = (field: string) => customer.matchedFields?.some((m) => m.field === field);


  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[480px] mx-4 max-h-[90vh] overflow-y-auto">
        {/* 标题区：品牌 teal + 装饰图标 */}
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-md shadow-teal-500/20">
              <UserPlus className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">认领我的客户档案</h3>
              <p className="text-sm text-slate-500">检测到系统中已存在与您匹配的客户档案</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {/* 说明提示（teal 信息条）：精简文案，合并结果与申诉出口一句话说明 */}
          <div className="p-3.5 bg-teal-50 border border-teal-200 rounded-lg flex gap-3">
            <Info className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
            <p className="text-sm text-teal-800">
              检测到与您匹配的客户档案（可能由伙伴或商家代为创建）；请结合档案照片与命中信息核对是否为本人，确认认领后将与您的账号关联合并。
            </p>
          </div>
          {/* 系统中的客户档案卡片（标题置于框内，与「您填写的信息」样式统一；命中信息以徽章显示在标题后） */}
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <div className="w-7 h-7 bg-slate-200 rounded-lg flex items-center justify-center">
                <User className="w-4 h-4 text-slate-600" />
              </div>
              <h4 className="text-sm font-medium text-slate-700">系统中已有的客户档案</h4>
              <MatchedFieldBadges fields={customer.matchedFields} />
            </div>
            <div className="flex items-center gap-4">
              <ZoomAvatarButton
                name={customer.fullName}
                avatarUrl={customer.avatar}
                size="xl"
                ring="ring-2 ring-teal-100"
                onZoom={() => setZoomAvatar(true)}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-semibold text-slate-800">{customer.fullName}</span>
                  {customer.username && (
                    <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full">已注册用户</span>
                  )}
                </div>
                <div className="text-sm text-slate-600">{customer.phone}</div>
              </div>
            </div>
            {(customer.backupPhone || customer.email || customer.idCard) && (
              <div className="mt-3 pt-3 border-t border-slate-200/70 space-y-2 text-sm">
                {customer.backupPhone && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">备用电话</span>
                    <span className="text-slate-800">{customer.backupPhone}</span>
                  </div>
                )}
                {customer.email && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">邮箱</span>
                    <span className="text-slate-800">{customer.email}</span>
                  </div>
                )}
                {customer.idCard && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">身份证</span>
                    <span className="text-slate-800">{maskIdCard(customer.idCard)}</span>
                  </div>
                )}
              </div>
            )}
            {!!customer.matchedFields?.length && (
              <p className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-200/70">
                若非本人，可选择短信验证申诉或提交人工审核。
              </p>
            )}
          </div>
          {/* 关联装饰 */}
          <div className="flex items-center gap-3 text-slate-300">
            <div className="flex-1 border-t border-dashed border-slate-200" />
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Link2 className="w-4 h-4 text-teal-500" />
              确认后与您的账号关联
            </div>
            <div className="flex-1 border-t border-dashed border-slate-200" />
          </div>
          {/* 您填写的信息卡片（补齐备用电话/邮箱，命中项打勾标记） */}
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-slate-200 rounded-lg flex items-center justify-center">
                <ClipboardList className="w-4 h-4 text-slate-600" />
              </div>
              <h4 className="text-sm font-medium text-slate-700">您填写的信息</h4>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-slate-500">姓名</span>
                <span className="text-slate-800">{formData.fullName}</span>
                {hitField('fullName') && <Check className="w-3.5 h-3.5 text-teal-500" />}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500">电话</span>
                <span className="text-slate-800">{formData.phone}</span>
                {hitField('phone') && <Check className="w-3.5 h-3.5 text-teal-500" />}
              </div>
              {formData.backupPhone && (
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">备用电话</span>
                  <span className="text-slate-800">{formData.backupPhone}</span>
                  {hitField('backupPhone') && <Check className="w-3.5 h-3.5 text-teal-500" />}
                </div>
              )}
              {formData.email && (
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">邮箱</span>
                  <span className="text-slate-800">{formData.email}</span>
                  {hitField('email') && <Check className="w-3.5 h-3.5 text-teal-500" />}
                </div>
              )}
              {formData.idCard && (
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">身份证</span>
                  <span className="text-slate-800">{maskIdCard(formData.idCard)}</span>
                  {hitField('idCard') && <Check className="w-3.5 h-3.5 text-teal-500" />}
                </div>
              )}
            </div>
          </div>
          {/* 覆盖提示（amber 警告条） */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              确认认领后，您注册时填写的信息将更新至该档案（以您本次填写的内容为准），档案中原有的对应信息将被覆盖。
            </p>
          </div>
        </div>
        <div className="p-6 border-t border-slate-100 flex gap-3">
          <button
            onClick={onMismatch}
            className="flex-1 py-3 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            非本人信息
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm shadow-teal-600/30"
          >
            确认识领
          </button>
        </div>
        {/* 头像放大预览（核对照片判定是否本人） */}
        {zoomAvatar && customer.avatar && (
          <ZoomPreviewLayer src={customer.avatar} alt={customer.fullName} onClose={() => setZoomAvatar(false)} />
        )}
      </div>
    </div>,
    document.body
  );
}